import React, { useMemo, useState } from 'react';
import {
  Pressable,
  StyleProp,
  StyleSheet,
  View,
  ViewStyle,
} from 'react-native';
import Icon, { IconKeyType } from 'shared/components/vxl/icon/Icon/Icon';
import BodyXSBoldText from 'shared/components/vxl/typography/primary/body/BodyXSBoldText';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import theme from 'shared/styles/theme';

export interface MapNodeProps {
  iconKey: IconKeyType;
  metric: string;
  labelText: string;
  priceText?: string;
  selected?: boolean;
  analyticsId?: string;
  customAttributes?: Record<string, string> | null;
  onPress: () => void;
  isDisabled?: boolean;
  style?: StyleProp<ViewStyle>;
  testID?: string;
  hideIcon?: boolean; // when true, render text-only pill (no icon)
}

interface ComposableStyle {
  pill: ViewStyle;
  iconWrapper: ViewStyle;
  icon: string;
}

interface MapNodeState {
  default: ComposableStyle;
  hovered: ComposableStyle;
  pressed: ComposableStyle;
  focused: ComposableStyle;
  disabled: ComposableStyle;
}

interface ComposableStyles {
  selected: MapNodeState;
  unselected: MapNodeState;
}

const composeMapNodeStyles = (
  isHovered: boolean,
  isPressed: boolean,
  isFocused: boolean,
  isDisabled: boolean,
  selected: boolean,
): ComposableStyle => {
  const styleGroup = selected
    ? composableStyles.selected
    : composableStyles.unselected;

  if (isDisabled) {
    return styleGroup.disabled;
  }

  if (isPressed) {
    return styleGroup.pressed;
  }

  if (isHovered) {
    return styleGroup.hovered;
  }

  if (isFocused) {
    return styleGroup.focused;
  }

  return styleGroup.default;
};

const MapNode = ({
  iconKey,
  metric,
  labelText,
  priceText,
  selected = false,
  analyticsId = '',
  customAttributes = null,
  onPress,
  isDisabled = false,
  style,
  testID,
  hideIcon = false,
}: MapNodeProps): React.ReactElement => {
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isPressed, setIsPressed] = useState(false);
  const [isKeyboardFocused, setIsKeyboardFocused] = useState(false);

  const buttonAnalytics = usePressableAnalytics({
    analyticsId,
    customAttributes,
  });

  const computedStyles = useMemo(
    (): ComposableStyle =>
      composeMapNodeStyles(
        isHovered,
        isPressed,
        isFocused,
        isDisabled,
        selected,
      ),
    [isHovered, isPressed, isFocused, isDisabled, selected],
  );

  const displayMetric = useMemo(() => {
    if (!hideIcon) {
      return metric;
    }

    return metric.trim();
  }, [hideIcon, metric]);

  const handlePress = (): void => {
    if (isDisabled) {
      return;
    }
    buttonAnalytics();
    onPress();
  };

  const handlePressIn = (): void => {
    if (isDisabled || isPressed) {
      return;
    }
    setIsPressed(true);
    setIsHovered(true);
    setIsKeyboardFocused(false);
  };

  const handlePressOut = (): void => {
    if (isDisabled) {
      return;
    }
    setIsPressed(false);
  };

  const handleHoverIn = (): void => {
    if (isDisabled || isPressed) {
      return;
    }
    setIsHovered(true);
  };

  const handleHoverOut = (): void => {
    if (isDisabled || isPressed) {
      return;
    }
    setIsHovered(false);
  };

  const handleFocus = (): void => {
    if (isDisabled || isPressed || isHovered) {
      return;
    }
    setIsFocused(true);
    setIsKeyboardFocused(true);
  };

  const handleBlur = (): void => {
    if (isDisabled) {
      return;
    }
    setIsFocused(false);
    setIsKeyboardFocused(false);
  };

  return (
    <View
      style={[
        styles.container,
        hideIcon ? styles.minimizedContainer : styles.fullContainer,
      ]}
    >
      <Pressable
        testID={testID}
        style={[style, styles.appearance]}
        onPress={handlePress}
        onHoverIn={handleHoverIn}
        onHoverOut={handleHoverOut}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onPressIn={handlePressIn}
        onPressOut={handlePressOut}
        disabled={isDisabled}
        aria-disabled={isDisabled}
        role="button"
      >
        <View
          style={[
            styles.pill,
            hideIcon ? styles.minimizedPill : null,
            computedStyles.pill,
            isKeyboardFocused ? styles.pillFocused : null,
          ]}
        >
          {!hideIcon ? (
            <View style={[styles.iconWrapper, computedStyles.iconWrapper]}>
              <Icon
                iconKey={iconKey}
                iconColor={computedStyles.icon}
                iconSize={18}
              />
            </View>
          ) : null}
          <BodyXSBoldText
            style={[
              styles.metricTextBase,
              selected ? styles.metricSelected : null,
              isDisabled ? styles.labelDisabled : null,
            ]}
            numberOfLines={1}
            ellipsizeMode={iconKey === 'GAS' ? 'middle' : 'tail'}
          >
            {displayMetric}
          </BodyXSBoldText>
        </View>
      </Pressable>
      {!hideIcon ? (
        <View style={styles.labelRow}>
          <BodyXSBoldText
            style={[
              styles.label,
              isDisabled ? styles.labelDisabled : null,
              priceText ? styles.labelWithSecondary : null,
            ]}
            numberOfLines={2}
          >
            {labelText}
          </BodyXSBoldText>
          {priceText ? (
            <BodyXSBoldText
              style={[
                styles.priceText,
                isDisabled ? styles.labelDisabled : null,
              ]}
              numberOfLines={1}
            >
              {priceText}
            </BodyXSBoldText>
          ) : null}
        </View>
      ) : null}
    </View>
  );
};

const composableStyles: ComposableStyles = {
  unselected: {
    default: {
      pill: {
        backgroundColor: theme.color.interactive.secondary,
        borderColor: 'rgba(0, 55, 67, 0.1)',
      },
      iconWrapper: {
        backgroundColor: theme.color.interactive.primary,
      },
      icon: theme.color.text.inverse,
    },
    hovered: {
      pill: {
        backgroundColor: theme.color.interactive.secondaryHovered,
        borderColor: 'rgba(0, 55, 67, 0.1)',
      },
      iconWrapper: {
        backgroundColor: theme.color.interactive.primary,
      },
      icon: theme.color.text.inverse,
    },
    pressed: {
      pill: {
        backgroundColor: theme.color.interactive.secondaryPressed,
        borderColor: 'rgba(0, 55, 67, 0.1)',
      },
      iconWrapper: {
        backgroundColor: theme.color.interactive.primary,
      },
      icon: theme.color.text.inverse,
    },
    focused: {
      pill: {
        backgroundColor: theme.color.interactive.secondary,
        borderColor: 'rgba(0, 55, 67, 0.1)',
      },
      iconWrapper: {
        backgroundColor: theme.color.interactive.primary,
      },
      icon: theme.color.text.inverse,
    },
    disabled: {
      pill: {
        backgroundColor: theme.color.interactive.secondary,
        borderColor: 'rgba(0, 55, 67, 0.1)',
      },
      iconWrapper: {
        backgroundColor: theme.color.interactive.disabled.text,
      },
      icon: theme.color.text.inverse,
    },
  },
  selected: {
    default: {
      pill: {
        backgroundColor: theme.color.interactive.primary,
      },
      iconWrapper: {
        backgroundColor: theme.color.interactive.primary,
      },
      icon: theme.color.text.inverse,
    },
    hovered: {
      pill: {
        backgroundColor: theme.color.interactive.primaryHovered,
      },
      iconWrapper: {
        backgroundColor: theme.color.interactive.primary,
      },
      icon: theme.color.text.inverse,
    },
    pressed: {
      pill: {
        backgroundColor: theme.color.interactive.primaryPressed,
      },
      iconWrapper: {
        backgroundColor: theme.color.interactive.primary,
      },
      icon: theme.color.text.inverse,
    },
    focused: {
      pill: {
        backgroundColor: theme.color.interactive.primary,
      },
      iconWrapper: {
        backgroundColor: theme.color.interactive.primary,
      },
      icon: theme.color.text.inverse,
    },
    disabled: {
      pill: {
        backgroundColor: theme.color.interactive.disabled.background,
      },
      iconWrapper: {
        backgroundColor: theme.color.interactive.disabled.background,
      },
      icon: theme.color.interactive.disabled.text,
    },
  },
};

const styles = StyleSheet.create({
  container: {
    alignItems: 'center',
    gap: theme.spacing.xs,
    width: 'auto',
  },
  fullContainer: {
    minWidth: 88,
    maxWidth: 100,
  },
  minimizedContainer: {
    gap: 0,
    minWidth: 0,
    maxWidth: 9999,
  },
  pill: {
    alignItems: 'center',
    borderColor: 'transparent',
    borderRadius: 999,
    borderWidth: 1,
    boxShadow: '0 0 14px 0 rgba(0, 0, 0, 0.11)',
    elevation: 2,
    flexDirection: 'row',
    gap: theme.spacing.xs,
    paddingLeft: theme.spacing.xs,
    paddingRight: theme.spacing.s,
    paddingVertical: theme.spacing.xs,
  },
  minimizedPill: {
    alignItems: 'center',
    borderColor: 'rgba(0, 55, 67, 0.1)',
    borderRadius: 30,
    gap: 4,
    justifyContent: 'center',
    paddingLeft: 6,
    paddingRight: 6,
    paddingVertical: 2,
  },
  pillFocused: {
    borderColor: theme.color.interactive.focused.border,
  },
  iconWrapper: {
    alignItems: 'center',
    borderRadius: 999,
    height: 28,
    justifyContent: 'center',
    width: 28,
  },
  metricTextBase: {
    color: theme.color.text.primary,
    flexShrink: 1,
  },
  metricSelected: {
    color: theme.color.interactive.secondary,
  },
  label: {
    color: theme.color.text.primary,
    flexShrink: 1,
    maxWidth: 100,
    minWidth: 0,
    textShadowColor: theme.color.text.inverse,
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 2,
    textAlign: 'center',
  },
  labelWithSecondary: {
    maxWidth: 60,
  },
  labelDisabled: {
    color: theme.color.text.muted,
  },
  labelRow: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.spacing.xs,
    justifyContent: 'center',
    left: 0,
    marginTop: theme.spacing.xs,
    position: 'absolute',
    right: 0,
    top: '100%',
  },
  priceText: {
    color: theme.color.text.primary,
    flexShrink: 0,
  },
  appearance: {
    border: 'none',
  },
});

export default MapNode;
